"use client";

import type { ConsultTableAnalyticsEventMap } from "@medimo/consult-table";
import { usePostHog } from "posthog-js/react";
import { useCallback } from "react";

type PostHogEventMap = {
  session_started: Record<string, never>;
  recording_duration_recorded: {
    consult_id: string;
    index: number;
    duration: number;
  };
  consult_created: { consult_id: string };
  summary_edited: { consult_id: string; version: number };
  summary_regenerated: { consult_id: string; version: number };
  summary_printed: { consult_id: string; version: number };
  pip_window_opened: { consult_id: string };
  pre_consultation_page_viewed: { consult_id: string };
  user_guide_viewed: { page_path: string };
  faq_viewed: { page_path: string };
  // AIチャットのエンゲージメント計測(MED-32)。chat_thread_started / answer_feedback_submitted は
  // backend(agent)が送るためここには含めない(二重計上防止)。質問・回答本文・患者情報は
  // 送らない。チャットイベントに診察 ID は載せず、診察との関連は chat_with_consult、
  // 入力起点は entry_point で見る。
  question_submitted: {
    thread_id: string;
    turn_number: number;
    // 送信の起点。現状は自由入力のみ実装済みのため "free_input" 固定(chip/suggestion 実装時に分岐)。
    entry_point: "free_input" | "chip" | "suggestion" | "notification";
    chat_with_consult: boolean;
  };
  answer_displayed: {
    thread_id: string;
    response_time_ms: number;
    citation_count: number;
  };
  // 出典リンクのクリック。source_type は出典の種別コードのみ(URL・タイトル等は送らない)。
  // union で受け、document-references の CitationSourceType が広がったら発火箇所で
  // 型エラーになる(集計が静かに壊れるのを防ぐ)。
  citation_clicked: { thread_id: string; source_type: "pubmed" | "domestic" };
  ai_chat_opened: {
    source: "sidebar" | "ai_consult_button";
    badge_shown: boolean;
  };
  // 診察一覧のページ送り・絞り込み(WEB-322)は consult-table が定義する
} & ConsultTableAnalyticsEventMap;

type PhaseOnePostHogEventName = keyof PostHogEventMap;

// 認証画面では super property の登録前で、ログアウト時にも reset されるため、
// wrapper を通る全イベントに platform を付ける。登録後は同じ値が重なるだけ。
// cognito_sub は AuthorizedPostHogSetup の identify で distinct_id として設定される。
export const usePostHogCapture = () => {
  const posthog = usePostHog();

  return useCallback(
    <EventName extends PhaseOnePostHogEventName>(
      eventName: EventName,
      properties: PostHogEventMap[EventName],
    ) => {
      posthog?.capture(eventName, { platform: "web", ...properties });
    },
    [posthog],
  );
};
